import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { FlatTreeControl } from "@angular/cdk/tree";

import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from "@angular/material/tree";
import { MatPaginator } from "@angular/material/paginator";

import { select, Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";

import {
  PageEvent,
  SortEvent,
  SearchEvent,
  FilterEvent,
  DefaultPage,
} from "src/app/models/custom-table/custom-table.model";
import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";
import { subMenusIterator } from "src/app/shared/utils/menuUtils";
import { getProductFamiliesList } from "src/app/store/actions/productFamilies/productFamilies.actions";
import {
  AppState,
  loadedFamilyExcelListState,
  loadedFamilyIsLoadingState,
  loadedFamilyListState,
  loadedFamilyTotalState,
  loadedMenusState,
} from "src/app/store/reducers";
import { ProductFamilyExtendedDTO } from "src/app/models/Products/productDTO";

interface FlatNode {
  expandable: boolean;
  code: string;
  level: number;
  description: string;
  nProducts: number;
}

@Component({
  selector: "app-product-family-list",
  templateUrl: "./product-family-list.component.html",
  styleUrls: ["./product-family-list.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ProductFamilyListComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  isLoading$: Observable<boolean>;
  productFamilyList$: Observable<ProductFamilyExtendedDTO[]>;
  excelProductFamilyList$: Observable<ProductFamilyExtendedDTO[]>;
  totalProductFamily$: Observable<number>;
  productFamilySub: Subscription;

  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  formPath: string = "families/family";
  tableTitle: string;
  headerIcon: string;

  defaultPageSize = DefaultPage.PAGE_SIZE;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private titleService: Title,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.isLoading$ = this.store.pipe(select(loadedFamilyIsLoadingState));
    this.productFamilyList$ = this.store.pipe(select(loadedFamilyListState));
    this.totalProductFamily$ = this.store.pipe(select(loadedFamilyTotalState));
    this.excelProductFamilyList$ = this.store.pipe(
      select(loadedFamilyExcelListState)
    );
    this.totalProductFamily$ = this.store.pipe(select(loadedFamilyTotalState));
    this.menus$ = this.store.pipe(select(loadedMenusState));

    this.loadScreenTitle();
    this.getProductFamily();
    this.productFamilySub = this.productFamilyList$.subscribe((family) => {
      if (family !== null && family !== undefined) {
        let familyNode = this.buildHierarchy(family);
        this.dataSource.data = familyNode;
      }
    });
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    });
  }

  displayedColumns: string[] = ["code", "nProducts"];

  private transformer = (node: any, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      code: node.product.code,
      description: node.product.description,
      nProducts: node?.product?.numberOfProducts
        ? node?.product?.numberOfProducts
        : 0,
      level: level,
    };
  };

  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this.transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: FlatNode) => node.expandable;

  buildHierarchy(list: any[]) {
    let roots = [],
      children = {};
    // find the top level nodes and hash the children based on parent
    for (let i = 0, len = list.length; i < len; ++i) {
      var item = list[i],
        parent = item.parentProductFamilyCode,
        target = !parent ? roots : children[parent] || (children[parent] = []);
      target.push({ product: item });
    }

    // function to recursively build the tree
    let findChildren = function (parent) {
      if (children[parent.product?.code]) {
        parent.children = children[parent.product.code];
        for (let i = 0, len = parent.children.length; i < len; ++i) {
          findChildren(parent.children[i]);
        }
      }
    };
    // enumerate through to handle the case where there are multiple roots
    for (let i = 0, len = roots.length; i < len; ++i) {
      findChildren(roots[i]);
    }
    return roots;
  }

  getProductFamily(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getProductFamiliesList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  changePage() {
    const pageEvent = this.getPageEvent(this.paginator);
    this.getProductFamily(pageEvent);
  }
  
  getPageEvent(paginator: MatPaginator) {
    const { pageIndex, pageSize } = paginator;
    return {
      pageIndex: pageIndex || DefaultPage.PAGE_INDEX,
      pageSize: pageSize || DefaultPage.PAGE_SIZE,
    };
  }

  openRow(row) {
    if ((row as any)?.code) {
      this.router.navigate([`${this.formPath}`, (row as any)?.code]);
    }
  }

  duplicateRow(row: any) {
    if (row) {
      localStorage.setItem("duplicate", `${row?.code}`);
      this.router.navigate([this.formPath]);
    }
  }

  newRow() {
    this.router.navigate([this.formPath]);
  }

  ngOnDestroy(): void {
    if (this.productFamilySub) {
      this.productFamilySub.unsubscribe();
    }
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
  }
}
